import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Bell, BellOff, Check, Eye, Gift, PackageMinus, PartyPopper, Trash2, CheckCheck, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useNotifications } from '@/hooks/useNotifications';
import type { Notification } from '@/types/database';
import { cn } from '@/lib/utils';

function NotificationItem({ 
  notification, 
  onMarkAsRead, 
  onDelete,
  onNavigate,
}: { 
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigate: (notificationId: string, type: string) => void;
}) {
  const getIcon = () => {
    switch (notification.type) {
      case 'approval':
        return <PartyPopper className="h-5 w-5 text-emerald-500" />;
      case 'rejection':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'view':
        return <Eye className="h-5 w-5 text-blue-500" />;
      case 'referral':
        return <Gift className="h-5 w-5 text-primary" />;
      // Estoque baixo (L10): a notificação nasce dentro da RPC
      // `apply_stock_movement` e leva `link: '/estoque'`, então o clique já cai
      // no ramo de `notification.link` abaixo — aqui só faltava o ícone.
      case 'low_stock':
        return <PackageMinus className="h-5 w-5 text-amber-500" />;
      default:
        return <Bell className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
    // All notification types with quote_id go to detail page
    if (notification.type === 'approval' || notification.type === 'rejection' || notification.type === 'view') {
      onNavigate(notification.id, notification.type);
    } else if (notification.link) {
      onNavigate(notification.link, 'link');
    }
  };

  const isApproval = notification.type === 'approval';
  const isRejection = notification.type === 'rejection';
  const highlightClass = isApproval 
    ? 'bg-emerald-50 border-l-4 border-l-emerald-500' 
    : isRejection 
      ? 'bg-red-50 border-l-4 border-l-red-500' 
      : '';

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 border-b transition-colors cursor-pointer hover:bg-muted/50',
        !notification.read && 'bg-primary/5',
        highlightClass
      )}
      onClick={handleClick}
    >
      <div className="flex-shrink-0 mt-0.5">
        {getIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn(
          'text-sm',
          !notification.read && 'font-semibold',
          isApproval && 'text-emerald-700',
          isRejection && 'text-red-700'
        )}>
          {notification.title}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
          {notification.message}
        </p>
        <p className="text-xs text-muted-foreground/70 mt-1">
          {formatDistanceToNow(new Date(notification.created_at), {
            addSuffix: true,
            locale: ptBR,
          })}
        </p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {!notification.read && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => {
              e.stopPropagation();
              onMarkAsRead(notification.id);
            }}
            title="Marcar como lida"
          >
            <Check className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(notification.id);
          }}
          title="Remover"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function NotificationCenter() {
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    loading, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification 
  } = useNotifications();

  const handleNavigate = (idOrLink: string, type: string) => {
    if (type === 'approval' || type === 'rejection' || type === 'view') {
      // Navigate to notification detail page
      navigate(`/notifications/${idOrLink}`);
    } else {
      // Navigate to the link directly
      navigate(idOrLink);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 text-xs bg-destructive text-destructive-foreground"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold text-sm">Notificações</h3>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={markAllAsRead}
              >
                <CheckCheck className="h-3.5 w-3.5 mr-1" />
                Marcar todas como lidas
              </Button>
            )}
          </div>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-4">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <BellOff className="h-7 w-7 text-muted-foreground/50" />
            </div>
            <p className="font-medium text-muted-foreground">Nenhuma notificação</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Por enquanto, está tudo em dia!
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-[400px]">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={markAsRead}
                onDelete={deleteNotification}
                onNavigate={handleNavigate}
              />
            ))}
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}
